package com.eladfinish.bible.contest.screens.search.mvi

import androidx.compose.ui.text.input.TextFieldValue
import com.eladfinish.bible.contest.arch.ActionKt
import com.eladfinish.bible.contest.models.ClickableTextData
import com.eladfinish.bible.contest.screens.search.ai.AiSearchSuggestion
import com.eladfinish.bible.contest.screens.search.history.SearchHistoryEntry
import com.eladfinish.bible.contest.screens.search.history.SearchHistorySort
import com.eladfinish.bible.contest.screens.search.model.HorizontalDirection
import com.eladfinish.bible.contest.screens.search.model.SearchSuggestion
import com.eladfinish.bible.contest.screens.search.model.VerseUiModel
import com.eladfinish.bible.contest.screens.search.suggestions.AutocompleteSuggestion
import com.eladfinish.bible.contest.screens.search.suggestions.PopularLists
import com.eladfinish.bible.contest.screens.search.suggestions.PopularSearchesFilter
import com.eladfinish.bible.contest.screens.search.suggestions.PopularSearchesList
import com.eladfinish.bible.contest.screens.search.suggestions.SuggestionsPage
import com.eladfinish.bible.contest.screens.search.usecases.specialSearch.SearchFact
import com.eladfinish.bible.contest.ui.menus.actions.SearchToolbarAction
import dev.icerock.moko.resources.desc.StringDesc
import kotlinx.collections.immutable.ImmutableList

sealed class SearchAction : ActionKt {

    data class Initialize(val initialText: String) : SearchAction()

    // Input
    data class TextFieldValueChanged(val textFieldValue: TextFieldValue) : SearchAction()
    data object ClearClicked : SearchAction()
    data object ClearLongClicked : SearchAction()
    data class SignClicked(val sign: String) : SearchAction()
    data class SignLongClicked(val sign: String) : SearchAction()
    data class HorizontalArrowClicked(val direction: HorizontalDirection) : SearchAction()
    data class HorizontalArrowLongClicked(val direction: HorizontalDirection) : SearchAction()

    // Search execution
    data object SearchClicked : SearchAction()
    data object SearchLongClicked : SearchAction()
    data object StopSearching : SearchAction()

    sealed class SearchProgressChanged : SearchAction() {
        abstract val book: String
        abstract val chapter: String
        abstract val progress: Int
        abstract val totalChapters: Int

        data class Regular(
            override val book: String,
            override val chapter: String,
            override val progress: Int,
            override val totalChapters: Int,
            val resultsCount: Int,
        ) : SearchProgressChanged()

        data class Special(
            override val book: String,
            override val chapter: String,
            override val progress: Int,
            override val totalChapters: Int,
            val title: StringDesc,
            val titleExtraData: StringDesc?,
        ) : SearchProgressChanged()
    }

    data class SearchResultsFound(
        val results: ImmutableList<VerseUiModel>,
        val generation: Int,
    ) : SearchAction()
    data class SearchCompleted(
        val regex: String,
        // Both counts are the finished run's own: the state's results list may not hold every
        // SearchResultsFound of the run yet when this action is reduced.
        val resultsCount: Int,
        val differentVersesCount: Int,
        val generation: Int,
        // An interesting fact's summary, shown as the search ends. It travels with this action
        // rather than in an action of its own: two dispatches can be reduced in either order,
        // and a completion reduced second would close the summary.
        val summaryDialog: SearchState.DialogState.General? = null,
    ) : SearchAction()
    data class SearchFailed(val message: StringDesc) : SearchAction()
    data class SearchSuggestionFound(val searchSuggestion: SearchSuggestion) : SearchAction()

    // Results
    data class ResultClicked(val item: VerseUiModel) : SearchAction()
    data class ResultLongClicked(val item: VerseUiModel) : SearchAction()
    data object ResultsByBookClicked : SearchAction()
    data class ResultsByBookItemClicked(val bookIndex: Int) : SearchAction()
    data object ResultsJumpHandled : SearchAction()
    data object ShowPreviousChapter : SearchAction()
    data object ShowNextChapter : SearchAction()

    // Toolbar / menu
    data class ToolbarClicked(val toolbarAction: SearchToolbarAction) : SearchAction()
    data class InterestingFactClicked(val searchFact: SearchFact) : SearchAction()
    data class SaveFile(val content: String) : SearchAction()

    // History
    data class HistoryLoaded(
        val entries: ImmutableList<SearchHistoryEntry>,
        val sort: SearchHistorySort,
        val openDialog: Boolean,
    ) : SearchAction()
    data class HistorySortChanged(val sort: SearchHistorySort) : SearchAction()
    data class HistoryEntryClicked(val entry: SearchHistoryEntry) : SearchAction()
    data class HistoryEntryDeleteClicked(val entry: SearchHistoryEntry) : SearchAction()
    data object ClearHistoryClicked : SearchAction()
    data object ClearHistoryCancelled : SearchAction()
    data object ClearHistoryConfirmed : SearchAction()

    // Autocomplete
    data class AutocompleteLoaded(
        val prefix: String,
        val suggestions: ImmutableList<AutocompleteSuggestion>,
    ) : SearchAction()
    data class PopularListsLoaded(val lists: PopularLists) : SearchAction()
    data class SuggestionsPageSelected(val page: SuggestionsPage) : SearchAction()
    data class AutocompleteSuggestionClicked(val suggestion: AutocompleteSuggestion, val page: SuggestionsPage) : SearchAction()
    data class AutocompleteSuggestionFillClicked(val suggestion: AutocompleteSuggestion, val page: SuggestionsPage) : SearchAction()
    data class MoreSuggestionsClicked(val filter: PopularSearchesFilter) : SearchAction()

    // Popular searches ("most searched" dialog)
    data class PopularSearchesLoaded(val list: PopularSearchesList) : SearchAction()
    data class PopularSearchesFilterChanged(val filter: PopularSearchesFilter) : SearchAction()
    data class PopularSearchClicked(val suggestion: AutocompleteSuggestion) : SearchAction()
    data class PopularSearchLongClicked(val suggestion: AutocompleteSuggestion) : SearchAction()

    // Nikud / Teamim section
    data object ToggleNikudAndTeamimSection : SearchAction()
    data object NikudAndTeamimSectionLongClicked : SearchAction()

    // Dialogs / Help
    data object HideDialog : SearchAction()
    data class ShowHelpDialog(val clickableTextData: ClickableTextData) : SearchAction()
    data class ToggleHelp(val shouldShowNextTime: Boolean) : SearchAction()
    data class ExampleClicked(val example: String) : SearchAction()

    // AI
    data object AiSearchClicked : SearchAction()
    data object ShowAiSearchDialog : SearchAction()

    sealed class AiPrompt : SearchAction() {
        data class Submit(val text: String) : AiPrompt()
        data class Succeeded(val suggestion: AiSearchSuggestion) : AiPrompt()
        data class Failed(val message: String) : AiPrompt()
    }

    // TODO: misc: should be sorted

    data class SearchSuggestionAccepted(
        val suggestedExpression: String,
    ) : SearchAction()
}
